import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { PdfPreviewModal } from "../components/PdfPreviewModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Printable wall poster: registration QR + one purchase QR per price-list item.
// Auto-scales on print so the whole poster always fits a single page.
export default function PosterPage() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState(null);
  const [printScale, setPrintScale] = useState(1);
  const [pdfDoc, setPdfDoc] = useState(null);
  const wrapRef = useRef(null);
  const origin = window.location.origin;

  const openPdf = async () => {
    try {
      const { data } = await axios.get(`${API}/site/poster.pdf`, { params: { origin }, responseType: "blob" });
      const blob = new Blob([data], { type: "application/pdf" });
      setPdfDoc({ blob, url: URL.createObjectURL(blob) });
    } catch { alert("Could not build the PDF — please try again."); }
  };
  const closePdf = () => { if (pdfDoc?.url) URL.revokeObjectURL(pdfDoc.url); setPdfDoc(null); };

  useEffect(() => {
    axios.get(`${API}/site/front-desk/items`).then((r) => setItems(r.data || []));
    axios.get(`${API}/settings`).then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const USABLE_PAGE_PX = 9.4 * 96; // letter height minus margins, at 96dpi
    const h = el.scrollHeight;
    setPrintScale(h > USABLE_PAGE_PX ? Math.max(0.45, (USABLE_PAGE_PX / h) * 0.98) : 1);
  }, [items, settings]);

  return (
    <div className="min-h-screen bg-white text-[#111] p-8 print:p-0" data-testid="poster-page">
      <style>{`@media print {
        .no-print, header { display: none !important; }
        @page { size: letter portrait; margin: 0.4in; }
        .poster-scale { zoom: ${printScale}; }
        .poster-scale table tr { page-break-inside: avoid; }
        .poster-row td { padding-top: 5px !important; padding-bottom: 5px !important; }
      }`}</style>

      <div className="max-w-3xl mx-auto poster-scale" ref={wrapRef}>
        <div className="no-print flex justify-end gap-2 mb-4">
          <button className="btn-secondary flex items-center gap-1.5" onClick={openPdf} data-testid="poster-pdf-btn">
            ⬇ Download PDF
          </button>
          <button className="btn-primary flex items-center gap-1.5" onClick={() => window.print()} data-testid="poster-print-btn">
            🖨 Print poster
          </button>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-extrabold font-display uppercase tracking-tight m-0">
              {settings?.cover_title || "The Self-Agency Project"}
            </h1>
            <p className="text-sm font-semibold italic mt-1 mb-6">Modern Safety. Real-World Fitness. Absolute Command.</p>
          </div>
          {settings?.logo_url && (
            <img src={settings.logo_url} alt="logo" className="h-16 shrink-0 object-contain" data-testid="poster-logo" />
          )}
        </div>

        <div className="flex items-center gap-5 border-2 border-black rounded-xl p-4 mb-8">
          <QRCodeSVG value={`${origin}/register`} size={130} level="H" data-testid="poster-register-qr" />
          <div>
            <div className="text-xl font-extrabold font-display uppercase">Step 1 — Registration</div>
            <p className="text-sm m-0 mt-1">New here? Scan to sign the training waiver on your phone.<br />
              <b>Sign it once — never again</b>, for any training, workshop or event.</p>
          </div>
        </div>

        {items.length > 0 && (
          <>
            <div className="text-xl font-extrabold font-display uppercase mb-3">Step 2 — Purchase Training</div>
            <table className="w-full border-collapse" data-testid="poster-items-table">
              <thead>
                <tr className="border-b-2 border-black text-left text-xs uppercase tracking-widest">
                  <th className="py-2 pr-3">Training</th>
                  <th className="py-2 px-3 text-center">Investment</th>
                  <th className="py-2 text-center">Get it</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-b border-gray-300 align-top poster-row" data-testid={`poster-item-${it.id}`}>
                    <td className="py-3 pr-3">
                      <div className="font-extrabold font-display">{it.name}</div>
                      {it.description && <div className="text-xs text-gray-700 mt-0.5 max-w-xs">{it.description}</div>}
                      {it.sessions > 0 && <div className="text-xs font-bold mt-0.5">({it.sessions} sessions)</div>}
                    </td>
                    <td className="py-3 px-3 font-extrabold font-display text-lg whitespace-nowrap text-center align-middle">${(it.price_cents / 100).toFixed(0)}</td>
                    <td className="py-3 text-center align-middle"><span className="inline-block"><QRCodeSVG value={`${origin}/buy/${it.id}`} size={92} level="M" /></span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        <p className="text-sm font-extrabold uppercase tracking-wide mt-8">Ready to take command? Scan to register, then scan your training.</p>
      </div>
      {pdfDoc && <PdfPreviewModal title="Front Desk Poster — PDF" filename="front-desk-poster.pdf"
        blob={pdfDoc.blob} url={pdfDoc.url} onClose={closePdf} />}
    </div>
  );
}
